import { createTransform } from "redux-persist";
import { type RootState } from "./store";
import { DEFAULT_ITEMS_PER_PAGE, inboxInitialState } from "./slices/inboxSlice";

type InboxStateT = RootState["inbox"];
type SearchParamsT = InboxStateT["searchParams"];

const FIRST_PAGE_NUM = "1";
const isInbox = window.location.pathname.includes("inbox");

const adIdViewCountsTransform = createTransform(
  // Transform state on its way to being serialized and persisted
  (inboundState: RootState["background"]) => ({
    campaignIdViewCounts: inboundState?.campaignIdViewCounts,
  }),
  // Transform state being rehydrated
  (outboundState: {
    campaignIdViewCounts: RootState["background"]["campaignIdViewCounts"];
  }) => ({
    campaignIdViewCounts: outboundState?.campaignIdViewCounts,
  }),
  {
    whitelist: ["background"],
  }
);

/**
 * Stores search params in the store.
 *
 * Get the search params from the URL if they exist,
 * otherwise use the ones or from the store either from the initial ones.
 */
const searchParamsTransform = createTransform(
  // Transform state on its way to being serialized and persisted
  (inboundState: InboxStateT) =>
    ({
      searchParams: inboundState.searchParams,
    } as InboxStateT),
  // Transform state being rehydrated
  (outboundState: InboxStateT) => {
    if (!isInbox) {
      return outboundState;
    }

    // Get the URL search params from the current URL
    const urlSearchParams = new URLSearchParams(window.location.search);

    // Initialize the search params object
    const searchParams: Partial<SearchParamsT> = {};

    // 't' param value from url
    const urlTypeParam = urlSearchParams.get("t") as SearchParamsT["t"];

    // Loop through the search param keys in the initial state
    for (const param in inboxInitialState.searchParams) {
      // Get the value from the URL, store, and initial state

      /* 1. */ const valueFromURL = urlSearchParams.get(param);

      /* 2. */ const valueFromStore =
        outboundState.searchParams[
          param as keyof typeof outboundState.searchParams
        ];

      // Don't save the search query to the store
      if ((valueFromStore as SearchParamsT["all"])?.q) {
        (valueFromStore as SearchParamsT["all"]).q = "";
      }

      /* 3. */ const initialValue =
        inboxInitialState.searchParams[param as keyof SearchParamsT];

      if (param === "t") {
        // Skip value from locale storage
        (searchParams as any)[param] = urlTypeParam || initialValue;

        continue;
      }

      if (param === "sortBy") {
        const value = urlSearchParams.get("sortBy");

        // Skip value from locale storage
        (searchParams as any)[param] = value || initialValue;

        continue;
      }

      if (param === "isSortAsc") {
        const value = urlSearchParams.get("asc") === "true";

        // Skip value from locale storage
        (searchParams as any)[param] =
          urlSearchParams.get("asc") !== null ? value : initialValue;

        continue;
      }

      if (param === "itemsPerPage") {
        const value = +(urlSearchParams.get("items") || 0);

        // Skip value from locale storage
        (searchParams as any)[param] = value || initialValue;

        continue;
      }

      for (const param in searchParams[urlTypeParam || "all"]) {
        const urlParam =
          param === "q" || param === "p"
            ? urlSearchParams.get(param)
            : urlSearchParams.get(param)?.split(",");
        const storeParam =
          //@ts-ignore
          outboundState.searchParams[urlTypeParam || "all"][param];
        const initialValue =
          //@ts-ignore
          inboxInitialState.searchParams[urlTypeParam || "all"][param];
        //@ts-ignore
        searchParams[urlTypeParam || "all"][param] =
          urlParam || storeParam || initialValue;
      }

      // Set the search params to the value from the URL if it exists,
      // otherwise use the value from the store or the initial value.
      searchParams[param as keyof SearchParamsT] = (valueFromURL ||
        valueFromStore ||
        initialValue) as any;
    }

    // Set all the search params from storage which is not exist in the URL, to the URL
    if (urlTypeParam) {
      // ---------------------------------------------------------------------------------
      // SET PAGE CATEGORY PARAM TO URL IF CURRENT URL DOESN'T HAVE IT BUT STORAGE HAS ---
      // ---------------------------------------------------------------------------------
      if (!urlSearchParams.has("f") && outboundState.searchParams.f) {
        urlSearchParams.set("f", outboundState.searchParams.f);
      }
      // ---------------------------------------------------------------------------------
      // ---------------------------------------------------------------------------------

      _writeStoredSearchParamsToUrl(
        urlTypeParam,
        outboundState,
        urlSearchParams
      );
    } else {
      // ---------------------------------------------------------------------------------
      // SET DEFAULT T PARAM TO URL AS "ALL" IF CURRENT URL DOESN'T HAVE IT --------------
      // ---------------------------------------------------------------------------------
      urlSearchParams.set("t", "all");
      // ---------------------------------------------------------------------------------
      // ---------------------------------------------------------------------------------

      _writeStoredSearchParamsToUrl("all", outboundState, urlSearchParams);
    }

    // Return the inbox initial state with the search params set
    return {
      ...inboxInitialState,
      searchParams: searchParams as SearchParamsT,
    };
  },
  {
    whitelist: ["inbox"],
  }
);

const _writeStoredSearchParamsToUrl = (
  pageType: SearchParamsT["t"] = "all",
  outboundState: InboxStateT,
  urlSearchParams: URLSearchParams
) => {
  // Get stored search params for the opened page
  const openedPageStoredParams = (outboundState.searchParams as SearchParamsT)[
    pageType
  ] as SearchParamsT["all"];

  // ---------------------------------------------------------------------------------
  // WRITE STORED PAGE PARAMS TO URL IF CURRENT URL DOESN'T HAVE IT ------------------
  // ---------------------------------------------------------------------------------
  for (const param in openedPageStoredParams) {
    if (param === "q") {
      continue;
    }
    const initialValue = (
      inboxInitialState.searchParams[pageType] as SearchParamsT["all"]
    )?.[param as keyof SearchParamsT["all"]];
    const storageValue =
      (openedPageStoredParams as Record<string, any>)[param] || initialValue;
    const urlValue = urlSearchParams.get(param);

    if (
      !urlValue &&
      (Array.isArray(storageValue) ? storageValue.length : storageValue)
    ) {
      urlSearchParams.set(
        param,
        Array.isArray(storageValue)
          ? storageValue.join(",")
          : String(storageValue)
      );
    }
  }
  // ---------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------

  // Update the URL with the page number
  if (!urlSearchParams.has("pn")) {
    urlSearchParams.set("pn", FIRST_PAGE_NUM);
  }

  // Set default sorting params to URL if they don't exist
  if (!urlSearchParams.has("sortBy")) {
    urlSearchParams.set("sortBy", "date");
  }
  if (!urlSearchParams.has("asc")) {
    urlSearchParams.set("asc", "true");
  }

  if (!urlSearchParams.has("items")) {
    urlSearchParams.set("items", String(DEFAULT_ITEMS_PER_PAGE));
  }

  // Update the URL with the search params
  const newUrl = `${window.location.pathname}?${urlSearchParams.toString()}`;
  window.history.pushState({}, "", newUrl);
};

export { adIdViewCountsTransform, searchParamsTransform };
